import { startTransition, useDeferredValue, useEffect, useState } from 'react';

import { Card } from '@lilith-arcana-os/ui';
import { browserRepositories, lilithDexie } from '@lilith-arcana-os/browser-storage';
import {
  CARD_PLACEHOLDERS,
} from '@lilith-arcana-os/tarot-corpus/assets/placeholders';
import {
  CARD_SEARCH_INDEX,
} from '@lilith-arcana-os/tarot-corpus/indexes/search-index';
import { DECK_ASSET_PACKS } from '@lilith-arcana-os/tarot-corpus/assets/asset-packs';
import { CARD_SUMMARIES } from '@lilith-arcana-os/tarot-corpus/derived/summaries';
import {
  type CardLibraryEntry,
  loadCardLibraryEntry,
} from '@lilith-arcana-os/tarot-corpus/library/load-card-library-entry';
import { TAROT_CORPUS_REGISTRY } from '@lilith-arcana-os/tarot-corpus/registry';

import { LibraryCachePanel } from '../components/library/LibraryCachePanel';
import { PageHeader } from '../components/ui/PageHeader';

interface CacheStats {
  readings: number;
  cards: number;
  spreads: number;
  queue: number;
}

const gradientMap: Record<string, string> = {
  'major-obsidian-gold': 'linear-gradient(145deg, rgba(10,10,15,1) 0%, rgba(201,169,110,0.4) 100%)',
  'embers-gold': 'linear-gradient(145deg, rgba(128,0,64,0.92) 0%, rgba(201,169,110,0.38) 100%)',
  'tide-rose': 'linear-gradient(145deg, rgba(50,57,78,0.95) 0%, rgba(201,110,139,0.42) 100%)',
  'mist-steel': 'linear-gradient(145deg, rgba(35,42,51,0.98) 0%, rgba(157,179,199,0.42) 100%)',
  'moss-coin': 'linear-gradient(145deg, rgba(30,35,24,0.98) 0%, rgba(138,160,109,0.42) 100%)',
};

const normalizeQuery = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export function LibraryPage() {
  const [stats, setStats] = useState<CacheStats>({
    readings: 0,
    cards: 0,
    spreads: 0,
    queue: 0,
  });
  const [query, setQuery] = useState('');
  const [selectedSlug, setSelectedSlug] = useState<string>(CARD_SUMMARIES[0]?.slug ?? '');
  const [selectedEntry, setSelectedEntry] = useState<CardLibraryEntry | null>(null);
  const [isLoadingEntry, setIsLoadingEntry] = useState(false);
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalizeQuery(deferredQuery);

  const filteredSummaries = CARD_SUMMARIES.filter((summary) => {
    if (normalizedQuery.length === 0) {
      return true;
    }

    const indexEntry = CARD_SEARCH_INDEX.find((entry) => entry.slug === summary.slug);

    return (
      normalizeQuery(summary.name).includes(normalizedQuery) ||
      summary.keywords.some((keyword) => normalizeQuery(keyword).includes(normalizedQuery)) ||
      indexEntry?.search_tokens.some((token) => token.includes(normalizedQuery)) === true
    );
  });

  useEffect(() => {
    let active = true;

    const load = async () => {
      const [readings, cards, spreads, queue] = await Promise.all([
        browserRepositories.readings.list(),
        browserRepositories.cards.list(),
        browserRepositories.spreads.list(),
        lilithDexie.sync_queue.toArray(),
      ]);

      if (!active) {
        return;
      }

      setStats({
        readings: readings.length,
        cards: cards.length,
        spreads: spreads.length,
        queue: queue.length,
      });
    };

    void load();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (filteredSummaries.length === 0) {
      return;
    }

    if (!filteredSummaries.some((summary) => summary.slug === selectedSlug)) {
      setSelectedSlug(filteredSummaries[0]?.slug ?? '');
    }
  }, [filteredSummaries, selectedSlug]);

  useEffect(() => {
    if (!selectedSlug) {
      setSelectedEntry(null);
      return;
    }

    let active = true;
    setIsLoadingEntry(true);

    void loadCardLibraryEntry(selectedSlug)
      .then((entry) => {
        if (!active) {
          return;
        }

        setSelectedEntry(entry ?? null);
      })
      .finally(() => {
        if (active) {
          setIsLoadingEntry(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedSlug]);

  const selectedSummary = filteredSummaries.find((summary) => summary.slug === selectedSlug) ?? filteredSummaries[0] ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        badge={`Corpus ${TAROT_CORPUS_REGISTRY.manifest.version}`}
        description="Biblioteca, ricerca e placeholder deck arrivano dal package tarot-corpus: contenuto versionato, validato e lazy-loadable."
        eyebrow="Biblioteca"
        title="Tarot corpus"
      />

      <LibraryCachePanel
        cards={stats.cards}
        queue={stats.queue}
        readings={stats.readings}
        spreads={stats.spreads}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card eyebrow="Core deck" title="Carte validate">
          <p className="text-4xl font-heading text-[var(--la-text)]">{TAROT_CORPUS_REGISTRY.coreCount}</p>
        </Card>
        <Card eyebrow="Longform" title="Entry biblioteca">
          <p className="text-4xl font-heading text-[var(--la-text)]">{TAROT_CORPUS_REGISTRY.libraryCount}</p>
        </Card>
        <Card eyebrow="Ricerca" title="Index entries">
          <p className="text-4xl font-heading text-[var(--la-text)]">{TAROT_CORPUS_REGISTRY.searchIndexCount}</p>
        </Card>
        <Card eyebrow="Asset packs" title="Pack registrati">
          <p className="text-4xl font-heading text-[var(--la-text)]">{DECK_ASSET_PACKS.length}</p>
        </Card>
      </div>

      <Card eyebrow="Search" title="Library explorer">
        <div className="grid gap-4">
          <input
            className="rounded-[1.5rem] border border-white/10 bg-[rgba(10,10,15,0.9)] px-4 py-3 text-[var(--la-text)] outline-none transition focus:border-[var(--la-gold)]"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cerca per nome, keyword, segno o tag."
            value={query}
          />
          <p className="text-sm text-[var(--la-text-muted)]">
            {filteredSummaries.length} carte visibili. Asset pack attivo di default: {TAROT_CORPUS_REGISTRY.manifest.default_asset_pack}.
          </p>
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="grid gap-3 sm:grid-cols-2">
          {filteredSummaries.map((summary) => {
            const placeholder = CARD_PLACEHOLDERS.find((entry) => entry.card_id === summary.card_id);
            const gradient = gradientMap[placeholder?.gradient_preset ?? 'major-obsidian-gold'];
            const selected = summary.slug === selectedSummary?.slug;

            return (
              <button
                className={`text-left transition ${selected ? 'translate-y-[-2px]' : ''}`}
                key={summary.slug}
                onClick={() =>
                  startTransition(() => {
                    setSelectedSlug(summary.slug);
                  })
                }
                type="button"
              >
                <Card eyebrow={summary.arcana === 'major' ? 'Arcano Maggiore' : summary.suit ?? 'Minore'} title={summary.name}>
                  <div
                    className="mb-4 rounded-[1.5rem] p-4"
                    style={{ background: gradient }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-3xl">{placeholder?.symbol_emoji ?? summary.symbol_emoji}</span>
                      <span className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--la-text-muted)]">
                        {placeholder?.badge ?? 'Carta'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-[var(--la-text-muted)]">{summary.summary_short}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {summary.keywords.slice(0, 3).map((keyword) => (
                      <span
                        className="rounded-full border border-white/10 px-3 py-1 text-xs text-[var(--la-text-muted)]"
                        key={keyword}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </Card>
              </button>
            );
          })}
        </div>

        <Card
          eyebrow={selectedSummary?.name ?? 'Dettaglio carta'}
          title={selectedEntry ? 'Library longform' : 'Nessuna carta selezionata'}
        >
          {isLoadingEntry ? (
            <p className="text-sm text-[var(--la-text-muted)]">Caricamento longform biblioteca…</p>
          ) : selectedEntry ? (
            <div className="grid gap-5 text-sm leading-relaxed text-[var(--la-text-muted)]">
              <p>{selectedEntry.summary_long}</p>
              <div>
                <h3 className="font-heading text-lg text-[var(--la-text)]">Diritta / rovesciata</h3>
                <p>{selectedEntry.upright_expanded}</p>
                <p className="mt-2">{selectedEntry.reversed_expanded}</p>
              </div>
              <div>
                <h3 className="font-heading text-lg text-[var(--la-text)]">Ambiti</h3>
                <p>{selectedEntry.love_expanded}</p>
                <p className="mt-2">{selectedEntry.work_expanded}</p>
                <p className="mt-2">{selectedEntry.spirit_expanded}</p>
                <p className="mt-2">{selectedEntry.psyche_expanded}</p>
              </div>
              <div>
                <h3 className="font-heading text-lg text-[var(--la-text)]">Simbolismo e cautela</h3>
                <p>{selectedEntry.symbolism_expanded}</p>
                <p className="mt-2">{selectedEntry.caution_notes}</p>
              </div>
              <div>
                <h3 className="font-heading text-lg text-[var(--la-text)]">Prompts</h3>
                <ul className="grid gap-2">
                  {selectedEntry.journaling_prompts.map((prompt) => (
                    <li key={prompt}>• {prompt}</li>
                  ))}
                  {selectedEntry.ritual_prompts.map((prompt) => (
                    <li key={prompt}>• {prompt}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[var(--la-text-muted)]">
              Seleziona una carta per caricare il longform dal chunk biblioteca.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
